import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  StarIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { translateApiError } from '../../utils/apiError';
import AuthPageShell from '../ui/AuthPageShell';
import InputIcon from '../ui/InputIcon';
import Button from '../ui/Button';
import BrandMark from '../ui/BrandMark';
import FormField from '../ui/FormField';
import { validateEmail, validateFields } from '../../utils/formValidation';
import { inputClassWithError } from '../../styles/forms';

function ForgotPassword() {
  const { t, formatMessage } = useLanguage();
  const fp = t.forgotPassword;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const validation = t.ux?.validation || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { valid, errors } = validateFields({
      email: validateEmail(email, {
        required: validation.emailRequired || 'Email is required',
        invalid: validation.emailInvalid || 'Enter a valid email address',
      }),
    });
    setFieldErrors(errors);
    if (!valid) return;

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage(fp.successMessage);
        toast.success(fp.recoveryLinkSent);
      } else {
        const errMsg = translateApiError(data, t, formatMessage, fp.couldNotProcess);
        setMessage(errMsg);
        toast.error(errMsg);
      }
    } catch (error) {
      setMessage(fp.connectionError);
      toast.error(fp.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: RocketLaunchIcon, title: fp.features.secure.title, description: fp.features.secure.desc },
    { icon: ShieldCheckIcon, title: fp.features.protection.title, description: fp.features.protection.desc },
    { icon: StarIcon, title: fp.features.support.title, description: fp.features.support.desc },
  ];

  return (
    <AuthPageShell backTo="/login" backLabel={fp.backToLogin}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-6 px-3 safe-x sm:gap-8 sm:px-4 lg:grid-cols-2 lg:gap-12"
      >
        {/* Left Side - Branding & Features */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center lg:text-left order-2 lg:order-1"
        >
          <div className="mb-8">
            <BrandMark variant="compact" className="mb-6" />
            
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-secondary-900 mb-4"
            >
              {fp.title}
              <span className="text-gradient block">{fp.titleHighlight}</span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg text-secondary-600 leading-relaxed max-w-md mx-auto lg:mx-0"
            >
              {fp.subtitle}
            </motion.p>
          </div>

          {/* Features */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="space-y-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="feature-row"
              >
                <div className="icon-tile h-12 w-12 from-orange-500 to-amber-500">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-secondary-900">{feature.title}</h3>
                  <p className="text-sm text-secondary-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right Side - Forgot Password Form */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="w-full max-w-md mx-auto order-1 lg:order-2"
        >
          <div className="auth-card p-6 sm:p-8 lg:p-10">
            {/* Form Header */}
            <div className="text-center mb-8">
              <motion.h3 
                className="text-2xl font-bold text-secondary-900 mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {fp.recoverPassword}
              </motion.h3>
              <motion.p 
                className="text-secondary-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {fp.sendLink}
              </motion.p>
            </div>

            {/* Form */}
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <FormField id="forgot-email" label={fp.email} error={fieldErrors.email} asChild>
                <InputIcon icon={EnvelopeIcon}>
                  <input
                    id="forgot-email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: null }));
                    }}
                    className={inputClassWithError(Boolean(fieldErrors.email), 'input-premium')}
                    placeholder={fp.enterEmail}
                    autoComplete="email"
                    aria-invalid={fieldErrors.email ? true : undefined}
                  />
                </InputIcon>
              </FormField>

              {/* Message Display */}
              {message && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-2xl text-sm ${
                    message.toLowerCase().includes('error') 
                      ? 'bg-red-100 text-red-700 border border-red-200' 
                      : 'bg-success-100 text-success-700 border border-success-200'
                  }`}
                >
                  {message}
                </motion.div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading}
                icon={ArrowRightIcon}
                iconPosition="right"
                className="w-full py-4"
              >
                {fp.sendRecoveryLink}
              </Button>

              {/* Back to Login */}
              <div className="text-center pt-4 border-t border-secondary-200/50">
                <Link
                  to="/login"
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors inline-flex items-center space-x-1"
                >
                  <span>{fp.backToLogin}</span>
                </Link>
              </div>
            </motion.form>
          </div>
        </motion.div>
      </motion.div>
    </AuthPageShell>
  );
}

export default ForgotPassword;